import { useRef } from "react";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum, round2, vals } from "@/lib/format";
import { ReportActions } from "@/components/tpv/ZReport";
import { payKey } from "@/lib/tpv";
import { FileImage } from "@/components/files/FileFields";

const Th = ({ children, end }) => <th className={`py-1 px-2 font-semibold text-[11px] text-[#333] bg-[#E3E3E3] border-b border-[#BDBDBD] ${end ? "text-end" : "text-start"}`}>{children}</th>;
const Td = ({ children, end, bold, dir }) => <td dir={dir} className={`py-1 px-2 border-b border-[#EDEDED] ${end ? "text-end" : "text-start"} ${bold ? "font-bold" : ""}`}>{children}</td>;
const Box = ({ children, className = "", testid }) => <div className={`border border-[#9A9A9A] rounded-sm p-3 ${className}`} data-testid={testid}>{children}</div>;

export default function InvoiceDoc({ inv }) {
  const { t, view, lang, money } = useApp();
  const ref = useRef(null);
  const shop = view.shop || {};
  const taxId = inv.tax_id ?? shop.tax_id;
  const rate = Number(inv.tax_rate ?? shop.tax_rate) || 0;
  const items = inv.items || [];
  const total = round2(inv.total || 0);
  const base = rate ? round2(total / (1 + rate / 100)) : total;
  const tax = round2(total - base);
  const sales = (inv.sale_ids || []).map((id) => view.sales?.[id]).filter(Boolean);
  const payments = sales.length
    ? sales.map((s) => ({ date: s.created_at, concept: `${t("ticket")} ${s.receipt_no || ""}`, method: t(payKey(s.payment)), amount: s.payment === "credit" ? 0 : s.payment === "split" ? Number(s.paid ?? s.total) : s.total, credit: s.payment === "credit" ? s.total : s.payment === "split" ? Math.max(0, s.total - Number(s.paid ?? s.total)) : 0 }))
    : inv.payment ? [{ date: inv.created_at, concept: t("fin_invoice"), method: t(payKey(inv.payment)), amount: inv.payment === "credit" ? 0 : total, credit: inv.payment === "credit" ? total : 0 }] : [];
  const debtPays = vals(view.payments).filter((p) => p.kind !== "refund" && (p.invoice_id === inv.id || (p.sale_id && (inv.sale_ids || []).includes(p.sale_id)))).map((p) => ({ date: p.created_at, concept: t("repayments"), method: t(payKey(p.method || "cash")), amount: Number(p.amount) }));
  const paid = round2(Math.min(total, payments.reduce((a, p) => a + p.amount, 0) + debtPays.reduce((a, p) => a + p.amount, 0)));
  const pending = round2(total - paid);
  const dueDays = Number(inv.due_days ?? shop.invoice_due_days) || 30;
  const due = new Date(inv.created_at); due.setDate(due.getDate() + dueDays);
  const grouped = items.reduce((m, i) => { const k = i.receipt_no || "—"; (m[k] ||= []).push(i); return m; }, {});
  return (
    <div className="space-y-3" data-testid="invoice-doc-wrap">
      <div ref={ref} className="print-area bg-white rounded-xl border border-border p-7 text-[12px] text-[#222] relative" data-testid="invoice-doc">
        {inv.status === "void" && <div className="absolute inset-x-0 top-1/3 text-center text-6xl font-black text-[#C7353B]/20 rotate-[-18deg] pointer-events-none">{t("fin_inv_void").toUpperCase()}</div>}
        {shop.logo_id && <div className="mb-3"><FileImage id={shop.logo_id} className="h-16 max-w-[180px] object-contain object-left" alt={shop.name} data-testid="invoice-logo" /></div>}
        <div className="grid grid-cols-[1.1fr_0.9fr_1.2fr] gap-4 items-start">
          <div className="leading-5" data-testid="invoice-issuer">
            <div className="font-extrabold text-sm uppercase">{shop.name}</div>
            {taxId && <div dir="ltr">{taxId}</div>}
            {shop.address && <div>{shop.address}</div>}
            {shop.phone && <div dir="ltr">{shop.phone}</div>}
          </div>
          <Box className="text-center self-start" testid="invoice-id-box">
            <div className="font-extrabold text-base tracking-wide uppercase">{t("fin_invoice")}</div>
            <div className="font-bold mt-1" dir="ltr">{inv.number}</div>
            <div dir="ltr">{fmtDate(inv.created_at, lang, false)}</div>
          </Box>
          <Box className="leading-5 min-h-[88px]" testid="invoice-customer-box">
            <div className="font-bold uppercase">{inv.customer_name}</div>
            {inv.customer_tax_id && <div dir="ltr">{inv.customer_tax_id}</div>}
            {inv.customer_address && <div>{inv.customer_address}</div>}
            {!inv.customer_tax_id && !inv.customer_address && <div className="text-[#777]">{t("customer")}</div>}
          </Box>
        </div>

        {Object.entries(grouped).map(([rcpt, rows]) => (
          <div key={rcpt} className="mt-5" data-testid="invoice-section">
            <div className="font-bold text-[12px] mb-1">{rcpt !== "—" ? `${t("ticket")} ${rcpt}` : t("fin_invoice_items")}{rows[0]?.date && <span className="font-normal text-[#666]"> · <span dir="ltr">{fmtDate(rows[0].date, lang, false)}</span></span>}</div>
            <table className="w-full">
              <thead><tr><Th>{t("concept")}</Th><Th end>{t("qty")}</Th><Th end>{t("price")}</Th><Th end>{t("subtotal")}</Th></tr></thead>
              <tbody>{rows.map((i, n) => <tr key={n} data-testid="invoice-line"><Td>{i.name}</Td><Td end dir="ltr">{fmtNum(i.qty)}</Td><Td end dir="ltr">{fmtNum(i.price)}</Td><Td end dir="ltr" bold>{fmtNum(round2(i.price * i.qty))}</Td></tr>)}</tbody>
            </table>
          </div>
        ))}

        <div className="mt-8 flex justify-center pdf-block">
          <table className="text-center" data-testid="invoice-tax-table">
            <thead><tr><Th>{t("tax_base")}</Th><Th>{t("tax")}</Th><Th>{t("tax_amount")}</Th><Th>{t("total")}</Th></tr></thead>
            <tbody><tr><td className="px-6 py-1" dir="ltr">{fmtNum(base)}</td><td className="px-6 py-1" dir="ltr">{rate ? `${t("tax_short")} ${fmtNum(rate)} %` : "—"}</td><td className="px-6 py-1" dir="ltr">{fmtNum(tax)}</td><td className="px-6 py-1 font-bold" dir="ltr">{money(total)}</td></tr></tbody>
          </table>
        </div>

        <div className="mt-5" data-testid="invoice-payments">
          <div className="font-bold mb-1">{t("paid")}</div>
          <table className="w-full">
            <tbody>
              {[...payments.filter((p) => p.amount > 0), ...debtPays].map((p, n) => <tr key={n} className="border-b border-[#EDEDED]"><td className="px-2 py-1 w-24" dir="ltr">{fmtDate(p.date, lang, false)}</td><td className="px-2 py-1">{p.concept}</td><td className="px-2 py-1">{p.method}</td><td className="px-2 py-1 text-end" dir="ltr">{money(p.amount)}</td></tr>)}
              {!payments.some((p) => p.amount > 0) && !debtPays.length && <tr><td className="px-2 py-1 text-[#777]">{t("fin_no_payments")}</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end pdf-block">
          <table className="min-w-[260px]" data-testid="invoice-totals">
            <tbody>
              <tr><td className="py-1 pe-6 font-extrabold">{t("fin_invoice_total")}</td><td className="py-1 text-end font-extrabold text-sm" dir="ltr" data-testid="invoice-doc-total">{money(total)}</td></tr>
              <tr><td className="py-1 pe-6">{t("paid")}</td><td className="py-1 text-end" dir="ltr" data-testid="invoice-doc-paid">{money(paid)}</td></tr>
              <tr className="border-t border-[#CFCFCF]"><td className="py-1 pe-6 font-bold">{t("pending")}</td><td className={`py-1 text-end font-bold ${pending > 0 ? "text-[#C7353B]" : ""}`} dir="ltr" data-testid="invoice-doc-pending">{money(pending)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-[11px]">{t("due_date")}: <b dir="ltr">{fmtDate(due.toISOString(), lang, false)}</b>{inv.note && <span className="ms-4 text-[#555]">{inv.note}</span>}</div>

        <div className="mt-8 text-end text-[10px] text-[#777]" data-pdf-pages>1 {t("of")} 1</div>
        <div className="mt-1 border-t border-[#DDD] pt-2 text-[8.5px] leading-[11px] text-[#555] text-justify whitespace-pre-line" data-testid="invoice-doc-footer">
          <div className="text-center font-semibold">{[shop.name, shop.address, taxId].filter(Boolean).join(" · ")}</div>
          {shop.doc_footer || t("fin_invoice_footer")} · {inv.staff_name}
        </div>
      </div>
      <ReportActions target={ref} name={`invoice-${inv.number}`} wide brand={false} />
    </div>
  );
}
